import apiService from './api.service';

const ASSET_EVENTS = {
  CREATE: 'asset:create',
  UPLOAD: 'asset:upload',
  UPDATE: 'asset:update',
  DELETE: 'asset:delete'
};

/**
 * Service for interacting with S3 through the backend API
 */
class S3ApiService {
  /**
   * Dispatch a custom asset event
   * 
   * @param {string} eventType - Type of event (from ASSET_EVENTS)
   * @param {Object} detail - Event details
   */
  static dispatchAssetEvent(eventType, detail) {
    try {
      const event = new CustomEvent(eventType, {
        bubbles: true,
        detail: {
          ...detail,
          timestamp: new Date().toISOString()
        }
      });

      document.dispatchEvent(event);
      console.log(`Asset event dispatched: ${eventType}`, detail);
    } catch (error) {
      console.error('Error dispatching asset event:', error);
    }
  }

  /**
   * Lists objects in an S3 bucket with the given prefix
   * 
   * @param {Object} options - List options
   * @param {string} options.prefix - The prefix to list objects under
   * @param {string} options.organizationName - Organization name
   * @param {string} options.sceneName - Scene name
   * @param {string} options.delimiter - Delimiter (default: '/')
   * @returns {Promise<Object>} - S3 listing result with CommonPrefixes (folders) and Contents (files)
   */
  static async listObjects(options = {}) {
    try {
      const params = {};
      if (options.prefix) params.prefix = options.prefix;
      if (options.organizationName) params.organizationName = options.organizationName;
      if (options.sceneName) params.sceneName = options.sceneName;
      if (options.delimiter) params.delimiter = options.delimiter;

      const response = await apiService.listS3Objects(params);

      if (!response.success) {
        throw new Error(response.message || 'Failed to list objects');
      }

      return response.data;
    } catch (error) {
      console.error('Error listing S3 objects:', error);
      throw error;
    }
  }

  /**
   * OPUS-1045: recursive object count + total bytes under a prefix.
   * @returns {Promise<{count:number,bytes:number,prefix:string}>}
   */
  static async getFolderStats(options = {}) {
    const params = {};
    if (options.prefix) params.prefix = options.prefix;
    if (options.organizationName) params.organizationName = options.organizationName;
    if (options.sceneName) params.sceneName = options.sceneName;
    const response = await apiService.getS3FolderStats(params);
    if (!response.success) {
      throw new Error(response.message || 'Failed to get folder stats');
    }
    return response.data;
  }

  /**
   * Creates a folder in S3
   * 
   * @param {Object} options - Folder creation options
   * @param {string} options.path - Folder path
   * @param {string} options.organizationName - Organization name
   * @param {string} options.sceneName - Scene name
   * @returns {Promise<Object>} - Result of the operation
   */
  static async createFolder(options = {}) {
    try {
      if (!options.path) {
        throw new Error('Folder path is required');
      }

      const response = await apiService.createS3Folder({
        path: options.path,
        organizationName: options.organizationName,
        sceneName: options.sceneName
      });

      if (!response.success) {
        throw new Error(response.message || 'Failed to create folder');
      }

      if (!options.suppressEvent) {
        this.dispatchAssetEvent(ASSET_EVENTS.CREATE, {
          type: 'folder',
          key: options.path,
          path: options.path,
          organizationName: options.organizationName,
          sceneName: options.sceneName,
          result: response.data
        });
      }

      return response.data;
    } catch (error) {
      console.error('Error creating S3 folder:', error);
      throw error;
    }
  }

  /**
   * Uploads a file to S3
   * 
   * @param {Object} options - Upload options
   * @param {File} options.file - File to upload
   * @param {string} options.path - Upload path
   * @param {string} options.organizationName - Organization name
   * @param {string} options.sceneName - Scene name
   * @param {Function} options.progressCallback - Progress callback (0-100)
   * @returns {Promise<Object>} - Upload result
   */
  static async uploadObject(options = {}) {
    try {
      const { file, path, organizationName, sceneName, progressCallback, suppressEvent } = options;
      if (!file) {
        throw new Error('File is required');
      }

      if (progressCallback) {
        progressCallback(0);
      }

      const result = await apiService.uploadToS3({
        file: file,
        path: path,
        organizationName: organizationName,
        sceneName: sceneName
      });

      if (!result.success) {
        throw new Error(result.message || 'Failed to upload file');
      }

      if (progressCallback) {
        progressCallback(100);
      }

      if (!suppressEvent) {
        this.dispatchAssetEvent(ASSET_EVENTS.UPLOAD, {
          type: 'file',
          key: result.data.key,
          fileName: file.name,
          fileSize: file.size,
          fileType: file.type,
          organizationName,
          sceneName,
          result: result.data
        });
      }

      return result.data;
    } catch (error) {
      console.error('Error uploading to S3:', error);
      throw error;
    }
  }

  /**
   * Gets a signed URL for an S3 object
   * 
   * @param {Object} options - Options
   * @param {string} options.key - The object key
   * @param {string} options.organizationName - Organization name
   * @param {string} options.sceneName - Scene name
   * @param {number} options.expiresIn - URL expiration in seconds (default: 3600)
   * @returns {Promise<string>} - Signed URL
   */
  static async getSignedUrl(options = {}) {
    try {
      if (!options.key) {
        throw new Error('Object key is required');
      }

      const params = {
        key: options.key
      };
      if (options.organizationName) params.organizationName = options.organizationName;
      if (options.sceneName) params.sceneName = options.sceneName;
      if (options.expiresIn) params.expiresIn = options.expiresIn;

      const response = await apiService.getSignedUrl(params);

      if (!response.success) {
        throw new Error(response.message || 'Failed to generate signed URL');
      }

      return response.data.url;
    } catch (error) {
      console.error('Error generating signed URL:', error);
      throw error;
    }
  }

  /**
   * Deletes an object from S3
   *
   * @param {Object} options - Delete options
   * @param {string} options.key - The object key
   * @param {string} options.organizationName - Organization name
   * @param {string} options.sceneName - Scene name
   * @returns {Promise<Object>} - Result of the operation
   */
  static async deleteObject({ key, organizationName, sceneName }) {
    try {
      if (!key) {
        throw new Error('Object key is required');
      }

      const response = await apiService.deleteFromS3(key, {
        organizationName,
        sceneName
      });

      if (!response.success) {
        throw new Error(response.message || 'Failed to delete object');
      }

      const isFolder = key.endsWith('/');
      this.dispatchAssetEvent(ASSET_EVENTS.DELETE, {
        type: isFolder ? 'folder' : 'file',
        key,
        path: key,
        organizationName,
        sceneName,
        result: response.data
      });

      return response.data;
    } catch (error) {
      console.error(`Error deleting ${key}:`, error);
      throw error;
    }
  }

  /**
   * Copy a single object or recursively copy a folder.
   * @param {Object} options
   * @param {string} options.sourceKey
   * @param {string} options.destinationKey
   * @param {boolean} [options.isFolder]
   * @param {string} options.organizationName
   * @param {string} options.sceneName
   */
  static async copyObject({ sourceKey, destinationKey, isFolder, organizationName, sceneName }) {
    if (!sourceKey || !destinationKey) throw new Error('sourceKey and destinationKey are required');
    const response = await apiService.copyS3Object({ sourceKey, destinationKey, isFolder, organizationName, sceneName });
    if (!response.success) throw new Error(response.message || 'Failed to copy object');
    this.dispatchAssetEvent(ASSET_EVENTS.UPDATE, {
      type: isFolder ? 'folder' : 'file',
      action: 'copy',
      sourceKey,
      destinationKey,
      organizationName,
      sceneName,
      result: response.data,
    });
    return response.data;
  }

  /**
   * Move/rename. Same shape as copyObject but deletes source after copy.
   */
  static async moveObject({ sourceKey, destinationKey, isFolder, organizationName, sceneName }) {
    if (!sourceKey || !destinationKey) throw new Error('sourceKey and destinationKey are required');
    const response = await apiService.moveS3Object({ sourceKey, destinationKey, isFolder, organizationName, sceneName });
    if (!response.success) throw new Error(response.message || 'Failed to move object');
    this.dispatchAssetEvent(ASSET_EVENTS.UPDATE, {
      type: isFolder ? 'folder' : 'file',
      action: 'move',
      sourceKey,
      destinationKey,
      organizationName,
      sceneName,
      result: response.data,
    });
    return response.data;
  }

  /**
   * Delete a mixed list of keys.
   * @param {Object} options
   * @param {Array<{key:string,isFolder?:boolean}|string>} options.keys
   * @param {string} options.organizationName
   * @param {string} options.sceneName
   */
  static async bulkDelete({ keys, organizationName, sceneName }) {
    if (!Array.isArray(keys) || keys.length === 0) throw new Error('keys array is required');
    const response = await apiService.bulkDeleteS3({ keys, organizationName, sceneName });
    if (!response.success) throw new Error(response.message || 'Failed bulk delete');
    this.dispatchAssetEvent(ASSET_EVENTS.DELETE, {
      type: 'bulk',
      keys,
      organizationName,
      sceneName,
      result: response.data,
    });
    return response.data;
  }

  /**
   * Reserve a one-shot download ticket from the server.
   * Pass a single sourceKey (with isFolder) OR a list of mixed keys for bulk
   * downloads. Server expands folders recursively and streams the result via
   * GET /s3/zip/<id>.
   *
   * Returns the downloadId (string). Build the URL with buildZipDownloadUrl().
   */
  static async prepareZipDownload({ sourceKey, isFolder, keys, organizationName, sceneName, filename } = {}) {
    if (!sourceKey && (!Array.isArray(keys) || keys.length === 0)) {
      throw new Error('Provide sourceKey or keys');
    }
    const response = await apiService.prepareZipDownload({
      sourceKey, isFolder, keys, organizationName, sceneName, filename,
    });
    if (!response.success) throw new Error(response.message || 'Failed to prepare download');
    return response.data.downloadId;
  }

  /** Build the GET URL for a prepared download. Suitable for window.location / <a href>. */
  static buildZipDownloadUrl(downloadId) {
    const base = (process.env.REACT_APP_SCENE_API_URL || '').replace(/\/$/, '');
    return `${base}/s3/zip/${encodeURIComponent(downloadId)}`;
  }

  /**
   * Gets metadata for a file without downloading it
   * 
   * @param {Object} options - Options
   * @param {string} options.key - The object key
   * @param {string} options.organizationName - Organization name
   * @param {string} options.sceneName - Scene name
   * @returns {Promise<Object>} - File metadata
   */
  static async getObjectMetadata(options = {}) {
    try {
      if (!options.key) {
        throw new Error('Object key is required');
      }

      const params = new URLSearchParams();
      params.append('key', options.key);
      if (options.organizationName) params.append('organizationName', options.organizationName);
      if (options.sceneName) params.append('sceneName', options.sceneName);

      const response = await this.makeRequest(`/metadata?${params.toString()}`);

      if (!response.success) {
        throw new Error(response.message || 'Failed to get object metadata');
      }

      return response.data;
    } catch (error) {
      console.error('Error getting S3 object metadata:', error);
      throw error;
    }
  }

  /**
   * Upload multiple files in parallel batches
   * 
   * @param {Object} options - Upload options
   * @param {Array<File>} options.files - Array of files to upload
   * @param {string} options.basePath - Base path for uploads
   * @param {string} options.organizationName - Organization name
   * @param {string} options.sceneName - Scene name
   * @param {number} options.concurrency - Max number of concurrent uploads (default: 5)
   * @param {Function} options.onProgress - Progress callback (percent, uploaded, total)
   * @param {Function} options.onFileComplete - Callback when a file completes (file, result)
   * @param {Function} options.onBatchComplete - Callback when a batch completes
   * @returns {Promise<Array>} - Results of all uploads
   */
  static async uploadFilesInParallel(options = {}) {
    const {
      files,
      basePath,
      organizationName,
      sceneName,
      concurrency = 5,
      onProgress,
      onFileComplete,
      onBatchComplete,
      preserveTopLevelFolder = true,
      trackAsBulk = false
    } = options;

    if (!files || !files.length) {
      throw new Error('Files array is required');
    }

    const total = files.length;
    let completed = 0;
    const results = [];
    let errors = [];

    let topLevelFolderName = '';
    if (preserveTopLevelFolder && files[0]?.webkitRelativePath) {
      const pathParts = files[0].webkitRelativePath.split('/');
      if (pathParts.length > 1) {
        topLevelFolderName = pathParts[0];
        console.log("Preserving top-level folder:", topLevelFolderName);
      }
    }

    const updateProgress = () => {
      const percent = Math.round((completed / total) * 100);
      if (onProgress) {
        onProgress(percent, completed, total);
      }
    };

    if (topLevelFolderName) {
      let cleanBasePath = basePath;
      if (cleanBasePath && cleanBasePath.startsWith('assets/')) {
        cleanBasePath = cleanBasePath.substring(6);
      } else if (cleanBasePath === 'assets') {
        cleanBasePath = '';
      }

      let topFolderPath = cleanBasePath
        ? `${cleanBasePath}/${topLevelFolderName}/`
        : `${topLevelFolderName}/`;

      topFolderPath = topFolderPath.replace(/\/+/g, '/');

      try {
        await this.createFolder({
          path: topFolderPath,
          organizationName,
          sceneName,
          suppressEvent: trackAsBulk
        });

        console.log("Created top-level folder:", topFolderPath);
      } catch (err) {
        console.log("Top-level directory creation skipped, may already exist:", topFolderPath);
      }
    }

    const processBatch = async (batch) => {
      const promises = batch.map(async (file) => {
        try {
          let uploadPath;

          if (file.webkitRelativePath) {
            let relativePath = file.webkitRelativePath;
            if (!preserveTopLevelFolder) {
              const pathParts = relativePath.split('/');
              pathParts.shift();
              relativePath = pathParts.join('/');
            }

            let cleanBasePath = '';
            if (basePath) {
              cleanBasePath = basePath.endsWith('/')
                ? basePath.slice(0, -1)
                : basePath;

              if (cleanBasePath.startsWith('assets/')) {
                cleanBasePath = cleanBasePath.substring(6);
              } else if (cleanBasePath === 'assets') {
                cleanBasePath = '';
              }

              uploadPath = `${cleanBasePath}/${relativePath}`;
            } else {
              uploadPath = relativePath;
            }

            const pathParts = relativePath.split('/');
            if (pathParts.length > 1) {
              const dirParts = pathParts.slice(0, -1);
              for (let i = 1; i <= dirParts.length; i++) {
                const dirPath = cleanBasePath
                  ? `${cleanBasePath}/${dirParts.slice(0, i).join('/')}/`
                  : `${dirParts.slice(0, i).join('/')}/`;

                const normalizedDirPath = dirPath.replace(/\/+/g, '/');

                try {
                  await this.createFolder({
                    path: normalizedDirPath,
                    organizationName,
                    sceneName,
                    suppressEvent: trackAsBulk
                  });
                } catch (err) {
                  console.log("Directory creation skipped, may already exist:", normalizedDirPath);
                }
              }
            }
          } else {
            let cleanBasePath = '';
            if (basePath) {
              cleanBasePath = basePath.endsWith('/')
                ? basePath.slice(0, -1)
                : basePath;

              if (cleanBasePath.startsWith('assets/')) {
                cleanBasePath = cleanBasePath.substring(6);
              } else if (cleanBasePath === 'assets') {
                cleanBasePath = '';
              }
            }

            uploadPath = cleanBasePath
              ? `${cleanBasePath}/${file.name}`
              : file.name;
          }

          const normalizedPath = uploadPath.replace(/\/+/g, '/');

          const result = await this.uploadObject({
            file,
            path: normalizedPath,
            organizationName,
            sceneName,
            suppressEvent: trackAsBulk,
            progressCallback: (fileProgress) => {
              // Individual file progress can be handled here if needed
            }
          });

          completed++;
          updateProgress();

          if (onFileComplete) {
            onFileComplete(file, result);
          }

          return result;
        } catch (error) {
          errors.push({ file: file.name, error });
          completed++;
          updateProgress();
          throw error;
        }
      });

      const batchResults = await Promise.allSettled(promises);

      if (onBatchComplete) {
        onBatchComplete(batchResults);
      }

      return batchResults;
    };

    const batches = [];
    for (let i = 0; i < files.length; i += concurrency) {
      batches.push(files.slice(i, i + concurrency));
    }

    updateProgress();

    for (const batch of batches) {
      const batchResults = await processBatch(batch);
      results.push(...batchResults);
    }

    if (trackAsBulk) {
      const successCount = results.filter(r => r.status === 'fulfilled').length;
      this.dispatchAssetEvent(ASSET_EVENTS.UPLOAD, {
        type: 'bulk',
        key: basePath || '',
        fileName: `${successCount} file${successCount !== 1 ? 's' : ''}`,
        organizationName,
        sceneName,
        fileCount: successCount,
        totalFiles: total
      });
    }
    return {
      total,
      completed,
      results,
      errors: errors.length ? errors : null
    };
  }

  /**
   * Get the S3 prefix for a caption's folder (under scene)
   * @param {string} captionId - Caption id (e.g. gfx_PermScore)
   * @param {string} version - Version (e.g. v1.0)
   * @returns {string} - Prefix like "captionId/version/"
   */
  static getCaptionPrefix(captionId, version) {
    const id = (captionId || '').toString().trim() || 'caption';
    const ver = (version || '').toString().trim() || 'v1.0';
    return `${id}/${ver}/`;
  }

  /**
   * Load a JSON file from a caption folder in S3
   * @param {Object} options
   * @param {string} options.organizationName
   * @param {string} options.sceneName
   * @param {string} options.captionId
   * @param {string} options.version
   * @param {string} options.fileName - e.g. lottieAnimation.json, gfx_PermScore.json
   * @returns {Promise<Object|null>} - Parsed JSON or null
   */
  static async loadCaptionJson(options = {}) {
    const { organizationName, sceneName, captionId, version, fileName } = options;
    if (!fileName) return null;
    const prefix = this.getCaptionPrefix(captionId, version);
    const key = `${prefix}${fileName}`;
    try {
      const baseUrl = (process.env.REACT_APP_SCENE_RENDERER_URL || '').replace(/\/$/, '');
      const url = `${baseUrl}/scenes/${organizationName}/${sceneName}/${key}`;
      const res = await fetch(url);
      if (!res.ok) return null;
      const text = await res.text();
      return JSON.parse(text);
    } catch (err) {
      console.error('Error loading caption file:', key, err);
      return null;
    }
  }

  /**
   * Save a JSON file to a caption folder in S3 (creates a blob and uploads)
   * @param {Object} options
   * @param {string} options.organizationName
   * @param {string} options.sceneName
   * @param {string} options.captionId
   * @param {string} options.version
   * @param {string} options.fileName
   * @param {Object|string} options.data - JSON object or string
   * @returns {Promise<Object>}
   */
  static async saveCaptionJson(options = {}) {
    const { organizationName, sceneName, captionId, version, fileName, data } = options;
    if (!fileName) throw new Error('fileName is required');
    const prefix = this.getCaptionPrefix(captionId, version);
    const key = `${prefix}${fileName}`;
    const str = typeof data === 'string' ? data : JSON.stringify(data, null, 2);
    const blob = new Blob([str], { type: 'application/json' });
    const file = new File([blob], fileName, { type: 'application/json' });
    return this.uploadObject({
      file,
      path: key,
      organizationName,
      sceneName,
    });
  }

  /**
   * List objects under a caption folder (e.g. to list assets, presets)
   * @param {Object} options
   * @param {string} options.organizationName
   * @param {string} options.sceneName
   * @param {string} options.captionId
   * @param {string} options.version
   * @param {string} [options.subPath] - e.g. "assets", "presets"
   * @returns {Promise<Object>} - { folders, files } from list response
   */
  static async listCaptionObjects(options = {}) {
    const { organizationName, sceneName, captionId, version, subPath = '' } = options;
    const prefix = this.getCaptionPrefix(captionId, version);
    const fullPrefix = subPath ? `${prefix}${subPath.replace(/\/$/, '')}/` : prefix;
    return this.listObjects({
      prefix: fullPrefix,
      organizationName,
      sceneName,
    });
  }

  /**
   * List version folders for a caption: base versions (e.g. v1.0) and archived (archive/1.12.1).
   * Use prefix scenes/org/scene/captionId/ to get subfolders; then list archive/ for archived versions.
   * @returns {{ baseVersions: string[], archivedVersions: string[] }}
   */
  static async listCaptionVersionFolders(options = {}) {
    const { organizationName, sceneName, captionId } = options;
    if (!organizationName || !sceneName || !captionId) {
      return { baseVersions: [], archivedVersions: [] };
    }
    const prefix = `scenes/${organizationName}/${sceneName}/${captionId}/`;
    try {
      const res = await this.listObjects({
        prefix,
        organizationName,
        sceneName,
        delimiter: '/',
      });
      const folders = (res.folders || []).map((f) => (f.name || '').replace(/\/$/, ''));
      const baseVersions = folders.filter((n) => n !== 'archive');
      let archivedVersions = [];
      if (folders.includes('archive')) {
        const archiveRes = await this.listObjects({
          prefix: `${prefix}archive/`,
          organizationName,
          sceneName,
          delimiter: '/',
        });
        archivedVersions = (archiveRes.folders || []).map((f) => (f.name || '').replace(/\/$/, ''));
      }
      return { baseVersions, archivedVersions };
    } catch (err) {
      console.error('Error listing caption version folders:', err);
      return { baseVersions: [], archivedVersions: [] };
    }
  }
}

export { ASSET_EVENTS };

export default S3ApiService; 